var kind        = require("enyo/kind"),
    logger      = require("enyo/logger"),
    UiComponent = require("enyo/UiComponent"),
    CanvasControl;

/**
 * An object describing the bounds of a
 * [CanvasControl]{@link enyo-canvas.CanvasControl} in canvas space.
 *
 * @typedef   {Object}  enyo-canvas.CanvasBounds
 * @property  {number}  x       The x coordinate of the top-left corner of the
 *                              Control in canvas space.
 * @property  {number}  y       The y coordinate of the top-left edge of the
 *                              Control in canvas space.
 * @property  {number}  width   The width of the control in canvas pixels
 * @property  {number}  height  The height of the control in canvas pixels
 */

/**
 * An object describing the bounds of a
 * [CanvasControl]{@link enyo-canvas.CanvasControl} in css pixels relative to
 * the top-left pixel of the canvas tag.
 *
 * @typedef   {Object}  enyo-canvas.CssBounds
 * @property  {number}  top     The distance from the top of the canvas in css
 *                              pixels
 * @property  {number}  left    The distance from the left of the canvas in css
 *                              pixels
 * @property  {number}  bottom  The distance from the bottom of the canvas in
 *                              css pixels
 * @property  {number}  right   The distance from the right of the canvas in css
 *                              pixels
 * @property  {number}  width   The width of the control in css pixels
 * @property  {number}  height  The height of the control in css pixels
 */

/**
 * [CanvasControl]{@link enyo-canvas.CanvasControl} is a specialized
 * `enyo.UiComponent` meant to represent an entity drawn on a
 * [Canvas]{@link enyo-canvas.Canvas}.
 *
 * @class   enyo-canvas.CanvasControl
 * @extends enyo.UiComponent
 * @public
 */
CanvasControl = kind(/** @lends enyo-canvas.CanvasControl.prototype */ {

    /**
     * @private
     */
    name: "enyo-canvas.CanvasControl",

    /**
     * @private
     */
    kind: UiComponent,

    /**
     * @private
     */
    defaultKind: null, // Set later, see below

    /**
     * The scene
     *
     * @type {?enyo-canvas.Canvas}
     * @public
     */
    scene: null,

    /**
     * Whether this control needs to be re-drawn.
     *
     * @type {boolean}
     * @public
     */
    dirty: true,

    /**
     * Whether this control needs to be destroyed on the next scene update.
     *
     * @type {boolean}
     * @public
     */
    expired: false,

    /**
     * Mark this control as dirty to ensure the new scene will render it. We
     * also force the changeHandler to fire, in order to make sure the dirty
     * state is applied to the scene as well.
     *
     * @private
     */
    sceneChanged: function() {
        this.set("dirty", true, true);
    },

    /**
     * Mark the scene as dirty if the child has turned dirty
     *
     * @private
     */
    dirtyChanged: function() {
        var scene = this.scene;

        if (scene && !scene.dirty && this.dirty) {
            scene.set("dirty", true);
        }
    },

    /**
     * Update scene dimensions. Extend to implement any required resizing logic.
     *
     * @abstract
     */
    reflow: function() {},

    /**
     * Implement a method here that returns the bounds of the control in canvas
     * space.
     *
     * @return {?enyo-canvas.CanvasBounds} The bounds, if applicable
     * @abstract
     */
    getCanvasBounds: function() {
        logger.error("CanvasControl must implement a `getCanvasBounds` method");
    },

    /**
     * Returns the bounds of the control in css pixels relative to the edges of
     * the canvas.
     *
     * @return {?enyo-canvas.CssBounds} The bounds, if applicable
     * @abstract
     */
    getCssBounds: function() {
        var canvasBounds = this.getCanvasBounds();

        return canvasBounds ? this.convertCanvasBoundsToCssBounds(canvasBounds) : null;
    },

    /**
     * Converts an x-coordinate in canvas space to the x-coordinate in css space
     * relative to the top-left corner of the canvas.
     *
     * @param  {number}  x  The canvas x-coordinate
     * @return {number}     The css x-coordinate
     * @public
     */
    convertCanvasXToCssX: function(x) {
        var scene    = this.scene || null,
            viewport = scene ? scene.viewport : null;

        return viewport ? x - viewport.x : null;
    },

    /**
     * Converts an y-coordinate in canvas space to the y-coordinate in css space
     * relative to the top-left corner of the canvas.
     *
     * @param  {number}  y  The canvas y-coordinate
     * @return {number}     The css y-coordinate
     * @public
     */
    convertCanvasYToCssY: function(y) {
        var scene    = this.scene || null,
            viewport = scene ? scene.viewport : null;

        return viewport ? y - viewport.y : null;
    },

    /**
     * Converts an [CanvasBounds]{@link enyo-canvas.CanvasBounds} object to an
     * [CssBounds]{@link enyo-canvas.CssBounds} object.
     *
     * @param  {enyo-canvas.CanvasBounds}  canvasBounds  The canvas bounds
     * @return {enyo-canvas.CssBounds}     The css bounds
     * @public
     */
    convertCanvasBoundsToCssBounds: function(canvasBounds) {
        var scene    = this.scene || null,
            viewport = scene ? scene.viewport : null;

        if (!viewport) {
            return null;
        }

        return {
            top:    this.convertCanvasYToCssY(canvasBounds.y),
            left:   this.convertCanvasXToCssX(canvasBounds.x),
            bottom: viewport.height - this.convertCanvasYToCssY(canvasBounds.y + canvasBounds.height),
            right:  viewport.width - this.convertCanvasXToCssX(canvasBounds.x + canvasBounds.width),
            width:  canvasBounds.width,
            height: canvasBounds.height
        };
    },

    /**
     * Converts an x-coordinate relative to the top-left corner of the canvas to
     * the x-coordinate in canvas space.
     *
     * @param  {number}  x  The css x-coordinate
     * @return {number}     The canvas x-coordinate
     * @public
     */
    convertCssXToCanvasX: function(x) {
        var scene    = this.scene || null,
            viewport = scene ? scene.viewport : null;

        return viewport ? x + viewport.x : null;
    },

    /**
     * Converts an y-coordinate relative to the top-left corner of the canvas to
     * the y-coordinate in canvas space.
     *
     * @param  {number}  y  The css y-coordinate
     * @return {number}     The canvas y-coordinate
     * @public
     */
    convertCssYToCanvasY: function(y) {
        var scene    = this.scene || null,
            viewport = scene ? scene.viewport : null;

        return viewport ? y + viewport.y : null;
    },

    /**
     * Converts an [CssBounds]{@link enyo-canvas.CssBounds} object to an
     * [CanvasBounds]{@link enyo-canvas.CanvasBounds} object.
     *
     * @param  {enyo-canvas.CssBounds}  cssBounds  The css bounds
     * @return {enyo-css.CanvasBounds}  The css bounds
     * @public
     */
    convertCssBoundsToCanvasBounds: function(cssBounds) {
        var scene    = this.scene || null,
            viewport = scene ? scene.viewport : null;

        if (!viewport) {
            return null;
        }

        return {
            x:      this.convertCssXToCanvasX(cssBounds.left),
            y:      this.convertCssYToCanvasY(cssBounds.top),
            width:  cssBounds.width,
            height: cssBounds.height
        };
    },

    /**
     * Update the state of the control in preparation for a draw action.
     *
     * @abstract
     */
    updateState: function() {},

    /**
     * Implement this method to draw something onto the rendering context.
     *
     * @param  {CanvasRenderingContext2D}  context  The rendering context
     * @abstract
     */
    draw: function() {}

});

CanvasControl.prototype.defaultKind = CanvasControl;
module.exports                      = CanvasControl;
